import React, { useState, useEffect, useContext, useReducer, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import NumberFormat from "react-number-format";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import PublishIcon from "@material-ui/icons/Publish";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import SettingsIcon from "@material-ui/icons/Settings";
import DateRangeIcon from "@material-ui/icons/DateRange";
import NightsStayIcon from "@material-ui/icons/NightsStay";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import holidayService from "../../services/holidayService";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// ── Reducer ──

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_HOLIDAYS":
      return action.payload;
    case "UPDATE_HOLIDAYS": {
      const holiday = action.payload;
      const idx = state.findIndex((s) => s.id === holiday.id);
      if (idx !== -1) {
        state[idx] = holiday;
        return [...state];
      }
      return [holiday, ...state];
    }
    case "DELETE_HOLIDAYS":
      return state.filter((h) => h.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

// ── Helpers ──

const TYPE_LABELS = {
  national: { label: "Nacional", color: "#1976d2" },
  state: { label: "Estadual", color: "#f57c00" },
  city: { label: "Municipal", color: "#388e3c" },
  company: { label: "Empresa", color: "#7b1fa2" },
};

const MONTHS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const formatDate = (day, month, year) => {
  const d = String(day).padStart(2, "0");
  const m = String(month).padStart(2, "0");
  return year ? `${d}/${m}/${year}` : `${d}/${m}`;
};

const formatDateRange = (holiday) => {
  const start = formatDate(holiday.day, holiday.month, holiday.year);
  if (holiday.endDay && holiday.endMonth) {
    const end = formatDate(holiday.endDay, holiday.endMonth, holiday.year);
    return `${start} - ${end}`;
  }
  return start;
};

const formatShifts = (shifts) => {
  if (!shifts || shifts.length === 0) return null;
  return shifts.map((s) => `${s.start}-${s.end}`).join(", ");
};

const getDaysInMonth = (month) => {
  if (!month) return 31;
  return new Date(2000, parseInt(month), 0).getDate();
};

// ── Styles ──

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 400,
  },
  infoText: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  headerButtons: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  typeBadge: {
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  defaultsSection: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  defaultsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    "&:hover": { opacity: 0.8 },
  },
  defaultsTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
  },
  tagHelp: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    background: theme.palette.action.hover,
    padding: theme.spacing(1),
    borderRadius: 4,
    fontFamily: "monospace",
  },
  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    flexWrap: "wrap",
  },
  shiftRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  importPreviewTable: {
    maxHeight: 400,
    overflow: "auto",
  },
  seedResultBox: {
    padding: theme.spacing(1.5),
    background: theme.palette.action.hover,
    borderRadius: 6,
    marginTop: theme.spacing(1),
  },
}));

// ── Component ──

const Holidays = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [holidays, dispatch] = useReducer(reducer, []);
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingHoliday, setDeletingHoliday] = useState(null);
  const [selectedHoliday, setSelectedHoliday] = useState(null);

  // Defaults section
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [defaultMessage, setDefaultMessage] = useState("");
  const [defaultShifts, setDefaultShifts] = useState([]);
  const [savingDefaults, setSavingDefaults] = useState(false);

  // Filter
  const [filterType, setFilterType] = useState("all");

  // Seed
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  // Seed State
  const [seedStateLoading, setSeedStateLoading] = useState(false);
  const [seedStateDialogOpen, setSeedStateDialogOpen] = useState(false);
  const [seedStateUf, setSeedStateUf] = useState("");
  const [seedStateResult, setSeedStateResult] = useState(null);

  // Import
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [importSource, setImportSource] = useState("brasilapi");
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importSelected, setImportSelected] = useState({});

  // Form state (modal)
  const [formDay, setFormDay] = useState("");
  const [formMonth, setFormMonth] = useState("");
  const [formYear, setFormYear] = useState("");
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [formType, setFormType] = useState("company");
  const [formState, setFormState] = useState("");
  const [formCity, setFormCity] = useState("");
  const [formIsRecurring, setFormIsRecurring] = useState(true);
  const [formDutyShifts, setFormDutyShifts] = useState([]);
  const [formUseDefault, setFormUseDefault] = useState(true);
  const [formIsActive, setFormIsActive] = useState(true);

  // Period (multi-day holiday)
  const [formIsPeriod, setFormIsPeriod] = useState(false);
  const [formEndDay, setFormEndDay] = useState("");
  const [formEndMonth, setFormEndMonth] = useState("");

  // Eve (special schedule day, not a holiday)
  const [formIsEve, setFormIsEve] = useState(false);
  const [formOverrideSchedule, setFormOverrideSchedule] = useState([]);

  // ── Data Loading ──

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await holidayService.list();
      dispatch({ type: "LOAD_HOLIDAYS", payload: data.holidays });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDefaults = useCallback(async () => {
    try {
      const { data } = await holidayService.getDefaults();
      setDefaultMessage(data.holidayDefaultMessage || "");
      setDefaultShifts(data.holidayDefaultDutyShifts || []);
    } catch (err) {
      // silently fail
    }
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    fetchHolidays();
    fetchDefaults();
  }, [fetchHolidays, fetchDefaults]);

  useEffect(() => {
    if (socket) {
      const handler = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_HOLIDAYS", payload: data.holiday });
        } else if (data.action === "delete") {
          dispatch({ type: "DELETE_HOLIDAYS", payload: data.holidayId });
        } else if (data.action === "seed" || data.action === "import") {
          fetchHolidays();
        }
      };
      socket.on(`company${user.companyId}-holiday`, handler);
      return () => socket.off(`company${user.companyId}-holiday`, handler);
    }
  }, [socket, user, fetchHolidays]);

  // ── Defaults ──

  const handleSaveDefaults = async () => {
    setSavingDefaults(true);
    try {
      await holidayService.updateDefaults({
        holidayDefaultMessage: defaultMessage,
        holidayDefaultDutyShifts: defaultShifts,
      });
      toast.success("Padroes de feriados salvos com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setSavingDefaults(false);
    }
  };

  // ── Seed ──

  const handleSeed = async () => {
    setSeedLoading(true);
    setSeedResult(null);
    try {
      const currentYear = new Date().getFullYear();
      const { data } = await holidayService.seed([currentYear, currentYear + 1]);
      setSeedResult(data);
      if (data.totalCreated > 0) {
        toast.success(`${data.totalCreated} feriados nacionais carregados!`);
        fetchHolidays();
      } else {
        toast.info("Todos os feriados nacionais ja estavam cadastrados.");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSeedLoading(false);
    }
  };

  // ── Seed State ──

  const handleSeedState = async () => {
    if (!seedStateUf) {
      toast.warn("Selecione um estado (UF).");
      return;
    }
    setSeedStateLoading(true);
    setSeedStateResult(null);
    try {
      const { data } = await holidayService.seedState(seedStateUf);
      setSeedStateResult(data);
      if (data.totalCreated > 0) {
        toast.success(`${data.totalCreated} feriados estaduais de ${seedStateUf} carregados!`);
        fetchHolidays();
      } else {
        toast.info(`Todos os feriados de ${seedStateUf} já estavam cadastrados.`);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSeedStateLoading(false);
    }
  };

  // ── Import ──

  const handleImportPreview = async () => {
    setImportLoading(true);
    setImportPreview(null);
    try {
      const { data } = await holidayService.importPreview(importYear, importSource);
      setImportPreview(data);
      const selected = {};
      data.holidays.forEach((h, i) => {
        if (!h.alreadyExists) selected[i] = true;
      });
      setImportSelected(selected);
    } catch (err) {
      toastError(err);
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportConfirm = async () => {
    if (!importPreview) return;
    const toImport = importPreview.holidays.filter((_, i) => importSelected[i]);
    if (toImport.length === 0) {
      toast.warn("Selecione pelo menos um feriado para importar.");
      return;
    }
    setImportLoading(true);
    try {
      const { data } = await holidayService.importConfirm(toImport);
      toast.success(`${data.created} feriados importados com sucesso!`);
      setImportDialogOpen(false);
      setImportPreview(null);
      fetchHolidays();
    } catch (err) {
      toastError(err);
    } finally {
      setImportLoading(false);
    }
  };

  // ── Holiday Modal ──

  const handleOpenModal = (holiday = null) => {
    if (holiday) {
      setSelectedHoliday(holiday);
      setFormDay(String(holiday.day));
      setFormMonth(String(holiday.month));
      setFormYear(holiday.year ? String(holiday.year) : "");
      setFormName(holiday.name || holiday.description || "");
      setFormDescription(holiday.description || "");
      setFormMessage(holiday.message || "");
      setFormType(holiday.type || "company");
      setFormState(holiday.state || "");
      setFormCity(holiday.city || "");
      setFormIsRecurring(holiday.isRecurring !== false);
      setFormDutyShifts(holiday.dutyShifts || []);
      setFormUseDefault(holiday.useDefault !== false);
      setFormIsActive(holiday.isActive !== false);
      setFormIsPeriod(!!holiday.endDay);
      setFormEndDay(holiday.endDay ? String(holiday.endDay) : "");
      setFormEndMonth(holiday.endMonth ? String(holiday.endMonth) : "");
      setFormIsEve(holiday.isEve === true);
      setFormOverrideSchedule(holiday.overrideSchedule || []);
    } else {
      resetForm();
    }
    setHolidayModalOpen(true);
  };

  const resetForm = () => {
    setSelectedHoliday(null);
    setFormDay("");
    setFormMonth("");
    setFormYear("");
    setFormName("");
    setFormDescription("");
    setFormMessage("");
    setFormType("company");
    setFormState("");
    setFormCity("");
    setFormIsRecurring(true);
    setFormDutyShifts([]);
    setFormUseDefault(true);
    setFormIsActive(true);
    setFormIsPeriod(false);
    setFormEndDay("");
    setFormEndMonth("");
    setFormIsEve(false);
    setFormOverrideSchedule([]);
  };

  const handleCloseModal = () => {
    setHolidayModalOpen(false);
    resetForm();
  };

  const handleSave = async () => {
    try {
      const holidayData = {
        day: parseInt(formDay),
        month: parseInt(formMonth),
        year: formYear ? parseInt(formYear) : null,
        name: formName,
        description: formDescription || formName,
        message: formUseDefault ? null : formMessage,
        type: formType,
        state: formType === "state" ? formState : null,
        city: formType === "city" ? formCity : null,
        isRecurring: formIsRecurring,
        dutyShifts: formUseDefault ? null : (formDutyShifts.length > 0 ? formDutyShifts : null),
        useDefault: formUseDefault,
        isActive: formIsActive,
        // Period fields
        endDay: formIsPeriod && formEndDay ? parseInt(formEndDay) : null,
        endMonth: formIsPeriod && formEndMonth ? parseInt(formEndMonth) : null,
        // Eve fields
        isEve: formIsEve,
        overrideSchedule: formIsEve && formOverrideSchedule.length > 0 ? formOverrideSchedule : null,
      };

      if (selectedHoliday) {
        await holidayService.update(selectedHoliday.id, holidayData);
        toast.success("Feriado atualizado com sucesso!");
      } else {
        await holidayService.create(holidayData);
        toast.success("Feriado criado com sucesso!");
      }
      handleCloseModal();
      fetchHolidays();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async (holidayId) => {
    try {
      await holidayService.remove(holidayId);
      toast.success("Feriado excluido com sucesso!");
    } catch (err) {
      toastError(err);
    }
    setConfirmModalOpen(false);
    setDeletingHoliday(null);
  };

  // ── Filter ──

  const filteredHolidays = holidays.filter((h) => {
    if (filterType === "all") return true;
    return h.type === filterType;
  });

  // ── Render ──

  const maxDay = getDaysInMonth(formMonth);
  const days = Array.from({ length: maxDay }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();

  return (
    <MainContainer>
      <MainHeader>
        <Title>Feriados</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.headerButtons}>
            <Tooltip title="Carregar feriados nacionais brasileiros (fixos + moveis)">
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSeed}
                startIcon={seedLoading ? <CircularProgress size={18} /> : <CloudDownloadIcon />}
                disabled={seedLoading}
                size="small"
              >
                Carregar nacionais
              </Button>
            </Tooltip>
            <Tooltip title="Carregar feriados estaduais por UF">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setSeedStateDialogOpen(true)}
                startIcon={<CloudDownloadIcon />}
                size="small"
              >
                Carregar estaduais
              </Button>
            </Tooltip>
            <Tooltip title="Importar feriados de API externa">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setImportDialogOpen(true)}
                startIcon={<PublishIcon />}
                size="small"
              >
                Importar da API
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleOpenModal()}
              startIcon={<AddIcon />}
              size="small"
            >
              Adicionar
            </Button>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* ── Seed Result ── */}
        {seedResult && (
          <Box className={classes.seedResultBox}>
            <Typography variant="body2">
              <strong>Resultado do seed:</strong> {seedResult.totalCreated} criados, {seedResult.totalSkipped} ja existiam
              {seedResult.createdNames?.length > 0 && (
                <span> — {seedResult.createdNames.join(", ")}</span>
              )}
            </Typography>
          </Box>
        )}

        {/* ── Defaults Section ── */}
        <Paper className={classes.defaultsSection} elevation={0}>
          <div className={classes.defaultsHeader} onClick={() => setDefaultsOpen(!defaultsOpen)}>
            <Typography className={classes.defaultsTitle}>
              <SettingsIcon fontSize="small" />
              Padrao para todos os feriados
            </Typography>
            {defaultsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
          <Collapse in={defaultsOpen}>
            <Box mt={2}>
              <Typography variant="body2" className={classes.infoText}>
                Configure a mensagem e os turnos de plantao padrao. Feriados com "Usar padrao" ativado
                usarao essas configuracoes automaticamente.
              </Typography>

              <TextField
                label="Mensagem padrao de feriado"
                value={defaultMessage}
                onChange={(e) => setDefaultMessage(e.target.value)}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                margin="dense"
                placeholder="Hoje estamos em feriado ({{holidayName}}). {{plantaoBlock}} Retornaremos no proximo dia util."
              />

              <Box className={classes.tagHelp} mt={1} mb={1}>
                Tags disponiveis: <strong>{"{{holidayName}}"}</strong> = nome do feriado | <strong>{"{{plantaoBlock}}"}</strong> = turnos de plantao formatados
              </Box>

              <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                Turnos de plantao padrao:
              </Typography>
              {defaultShifts.length === 0 && (
                <Typography variant="body2" className={classes.infoText}>
                  Sem turnos = fechado o dia todo
                </Typography>
              )}
              {defaultShifts.map((shift, i) => (
                <div key={i} className={classes.shiftRow}>
                  <NumberFormat
                    value={shift.start || ""}
                    variant="outlined"
                    margin="dense"
                    customInput={TextField}
                    format="##:##"
                    label="Inicio"
                    size="small"
                    style={{ width: 120 }}
                    onValueChange={(v) => {
                      const updated = [...defaultShifts];
                      updated[i] = { ...updated[i], start: v.formattedValue };
                      setDefaultShifts(updated);
                    }}
                  />
                  <NumberFormat
                    value={shift.end || ""}
                    variant="outlined"
                    margin="dense"
                    customInput={TextField}
                    format="##:##"
                    label="Fim"
                    size="small"
                    style={{ width: 120 }}
                    onValueChange={(v) => {
                      const updated = [...defaultShifts];
                      updated[i] = { ...updated[i], end: v.formattedValue };
                      setDefaultShifts(updated);
                    }}
                  />
                  <IconButton size="small" onClick={() => setDefaultShifts(defaultShifts.filter((_, j) => j !== i))} style={{ color: "#e53935" }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setDefaultShifts([...defaultShifts, { start: "", end: "" }])}
                disabled={defaultShifts.length >= 6}
              >
                Adicionar turno
              </Button>

              <Box mt={2} textAlign="right">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSaveDefaults}
                  disabled={savingDefaults}
                >
                  {savingDefaults ? <CircularProgress size={18} /> : "Salvar padroes"}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Paper>

        {/* ── Filter Bar ── */}
        <div className={classes.filterBar}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Filtrar por tipo</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Filtrar por tipo"
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="national">Nacional</MenuItem>
              <MenuItem value="state">Estadual</MenuItem>
              <MenuItem value="city">Municipal</MenuItem>
              <MenuItem value="company">Empresa</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            {filteredHolidays.length} feriado{filteredHolidays.length !== 1 ? "s" : ""}
          </Typography>
        </div>

        {/* ── Holiday Table ── */}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Data</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell align="center">Tipo</TableCell>
              <TableCell align="center">Atendimento</TableCell>
              <TableCell align="center">Mensagem</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={7} />}
            {!loading && filteredHolidays.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum feriado cadastrado. Use "Carregar nacionais" para comecar.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filteredHolidays
                .sort((a, b) => {
                  if (a.month !== b.month) return a.month - b.month;
                  return a.day - b.day;
                })
                .map((holiday) => {
                  const typeInfo = TYPE_LABELS[holiday.type] || TYPE_LABELS.company;
                  const shifts = holiday.dutyShifts;
                  const shiftsText = formatShifts(shifts);

                  return (
                    <TableRow key={holiday.id}>
                      <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                        {formatDateRange(holiday)}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                          {holiday.name || holiday.description || "-"}
                          {holiday.isRecurring && (
                            <Tooltip title="Recorrente (todo ano)">
                              <span style={{ fontSize: "0.7rem", color: "#999" }}>&#8635;</span>
                            </Tooltip>
                          )}
                          {holiday.endDay && (
                            <Tooltip title={`Periodo: ${formatDateRange(holiday)}`}>
                              <DateRangeIcon style={{ fontSize: 16, color: "#1976d2", marginLeft: 2 }} />
                            </Tooltip>
                          )}
                          {holiday.isEve && (
                            <Tooltip title="Vespera - horario especial">
                              <NightsStayIcon style={{ fontSize: 16, color: "#ff9800", marginLeft: 2 }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            typeInfo.label +
                            (holiday.state ? ` ${holiday.state}` : "") +
                            (holiday.city ? ` ${holiday.city}` : "")
                          }
                          size="small"
                          className={classes.typeBadge}
                          style={{
                            backgroundColor: typeInfo.color,
                            color: "#fff",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {holiday.isEve ? (
                          holiday.overrideSchedule && holiday.overrideSchedule.length > 0 ? (
                            <Tooltip title={`Vespera: ${formatShifts(holiday.overrideSchedule)}`}>
                              <Chip label="vespera" size="small" style={{ backgroundColor: "#ff9800", color: "#fff" }} />
                            </Tooltip>
                          ) : (
                            <Chip label="vespera fechada" size="small" style={{ backgroundColor: "#ff9800", color: "#fff" }} />
                          )
                        ) : holiday.useDefault ? (
                          <Chip label="padrao" size="small" variant="outlined" />
                        ) : shiftsText ? (
                          <Tooltip title={shiftsText}>
                            <Chip label="customizado" size="small" color="secondary" />
                          </Tooltip>
                        ) : (
                          <Chip label="fechado" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {holiday.useDefault || !holiday.message ? (
                          <Chip label="padrao" size="small" variant="outlined" />
                        ) : (
                          <Tooltip title={holiday.message?.substring(0, 100)}>
                            <Chip label="customizado" size="small" color="secondary" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={holiday.isActive ? "Ativo" : "Inativo"}
                          color={holiday.isActive ? "primary" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenModal(holiday)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeletingHoliday(holiday);
                            setConfirmModalOpen(true);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </Paper>

      {/* ═══ Holiday Edit/Create Modal ═══ */}
      <Dialog open={holidayModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedHoliday ? "Editar feriado" : "Novo feriado"}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {/* Nome */}
          <TextField
            label="Nome do feriado"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            required
            placeholder="Ex: Natal, Corpus Christi..."
          />

          {/* Data */}
          <Box display="flex" gap={1}>
            <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
              <InputLabel>Mes</InputLabel>
              <Select
                value={formMonth}
                onChange={(e) => {
                  setFormMonth(e.target.value);
                  const newMax = getDaysInMonth(e.target.value);
                  if (formDay && parseInt(formDay) > newMax) setFormDay(String(newMax));
                }}
                label="Mes"
              >
                {MONTHS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
              <InputLabel>Dia</InputLabel>
              <Select value={formDay} onChange={(e) => setFormDay(e.target.value)} label="Dia" disabled={!formMonth}>
                {days.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Ano (vazio = fixo)"
              value={formYear}
              onChange={(e) => setFormYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
              variant="outlined"
              size="small"
              style={{ flex: 1 }}
              placeholder="2026"
              helperText="Vazio = todos os anos"
            />
          </Box>

          {/* ── Periodo (multi-day) ── */}
          <Box
            style={{
              border: formIsPeriod ? "1px solid #1976d2" : "1px solid #e0e0e0",
              borderRadius: 8,
              padding: "8px 12px",
              background: formIsPeriod ? "rgba(25,118,210,0.04)" : "transparent",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formIsPeriod}
                  onChange={(e) => {
                    setFormIsPeriod(e.target.checked);
                    if (!e.target.checked) {
                      setFormEndDay("");
                      setFormEndMonth("");
                    } else if (formMonth && !formEndMonth) {
                      setFormEndMonth(formMonth);
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                  <DateRangeIcon fontSize="small" style={{ color: "#1976d2" }} />
                  <span>Periodo (feriado prolongado)</span>
                </Box>
              }
            />
            {formIsPeriod && (
              <>
                <Typography variant="body2" style={{ color: "#666", fontSize: "0.8rem", marginBottom: 8 }}>
                  Ex: Carnaval (01/03 a 05/03), Semana Santa, etc.
                </Typography>
                <Box display="flex" gap={1}>
                  <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                    <InputLabel>Mes fim</InputLabel>
                    <Select
                      value={formEndMonth}
                      onChange={(e) => {
                        setFormEndMonth(e.target.value);
                        const newMax = getDaysInMonth(e.target.value);
                        if (formEndDay && parseInt(formEndDay) > newMax) setFormEndDay(String(newMax));
                      }}
                      label="Mes fim"
                    >
                      {MONTHS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                    <InputLabel>Dia fim</InputLabel>
                    <Select
                      value={formEndDay}
                      onChange={(e) => setFormEndDay(e.target.value)}
                      label="Dia fim"
                      disabled={!formEndMonth}
                    >
                      {Array.from({ length: getDaysInMonth(formEndMonth) }, (_, i) => i + 1).map((d) => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}
          </Box>

          {/* ── Vespera (special schedule) ── */}
          <Box
            style={{
              border: formIsEve ? "1px solid #ff9800" : "1px solid #e0e0e0",
              borderRadius: 8,
              padding: "8px 12px",
              background: formIsEve ? "rgba(255,152,0,0.04)" : "transparent",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formIsEve}
                  onChange={(e) => {
                    setFormIsEve(e.target.checked);
                    if (!e.target.checked) setFormOverrideSchedule([]);
                  }}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                  <NightsStayIcon fontSize="small" style={{ color: "#ff9800" }} />
                  <span>Vespera (horario especial)</span>
                </Box>
              }
            />
            {formIsEve && (
              <>
                <Typography variant="body2" style={{ color: "#666", fontSize: "0.8rem", marginBottom: 8 }}>
                  Nao e feriado, mas tem horario diferenciado (ex: vespera de Natal, vespera de Ano Novo).
                  Sem turnos = fechado o dia todo.
                </Typography>
                <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
                  Horario especial da vespera:
                </Typography>
                {formOverrideSchedule.length === 0 && (
                  <Typography variant="body2" style={{ color: "#999", fontSize: "0.8rem", marginBottom: 4 }}>
                    Sem turnos = fechado o dia todo (enviar mensagem de feriado)
                  </Typography>
                )}
                {formOverrideSchedule.map((shift, i) => (
                  <div key={i} className={classes.shiftRow}>
                    <NumberFormat
                      value={shift.start || ""}
                      variant="outlined"
                      margin="dense"
                      customInput={TextField}
                      format="##:##"
                      label="Inicio"
                      size="small"
                      style={{ width: 120 }}
                      onValueChange={(v) => {
                        const updated = [...formOverrideSchedule];
                        updated[i] = { ...updated[i], start: v.formattedValue };
                        setFormOverrideSchedule(updated);
                      }}
                    />
                    <NumberFormat
                      value={shift.end || ""}
                      variant="outlined"
                      margin="dense"
                      customInput={TextField}
                      format="##:##"
                      label="Fim"
                      size="small"
                      style={{ width: 120 }}
                      onValueChange={(v) => {
                        const updated = [...formOverrideSchedule];
                        updated[i] = { ...updated[i], end: v.formattedValue };
                        setFormOverrideSchedule(updated);
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setFormOverrideSchedule(formOverrideSchedule.filter((_, j) => j !== i))}
                      style={{ color: "#e53935" }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setFormOverrideSchedule([...formOverrideSchedule, { start: "", end: "" }])}
                  disabled={formOverrideSchedule.length >= 4}
                >
                  Adicionar turno
                </Button>
              </>
            )}
          </Box>

          {/* Tipo */}
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select value={formType} onChange={(e) => setFormType(e.target.value)} label="Tipo">
              <MenuItem value="national">Nacional</MenuItem>
              <MenuItem value="state">Estadual</MenuItem>
              <MenuItem value="city">Municipal</MenuItem>
              <MenuItem value="company">Empresa</MenuItem>
            </Select>
          </FormControl>

          {formType === "state" && (
            <TextField
              label="Estado (UF)"
              value={formState}
              onChange={(e) => setFormState(e.target.value.toUpperCase().slice(0, 2))}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="SP"
            />
          )}
          {formType === "city" && (
            <>
              <TextField
                label="Estado (UF)"
                value={formState}
                onChange={(e) => setFormState(e.target.value.toUpperCase().slice(0, 2))}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="SP"
              />
              <TextField
                label="Cidade"
                value={formCity}
                onChange={(e) => setFormCity(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Sao Paulo"
              />
            </>
          )}

          <Divider />

          {/* Use default toggle */}
          <FormControlLabel
            control={
              <Switch checked={formUseDefault} onChange={(e) => setFormUseDefault(e.target.checked)} color="primary" />
            }
            label="Usar mensagem e turnos padrao da empresa"
          />

          {!formUseDefault && (
            <>
              {/* Custom message */}
              <TextField
                label="Mensagem customizada"
                value={formMessage}
                onChange={(e) => setFormMessage(e.target.value)}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                margin="dense"
                placeholder="Mensagem especifica para este feriado..."
              />

              {/* Custom duty shifts */}
              <Typography variant="subtitle2">Turnos de plantao:</Typography>
              {formDutyShifts.length === 0 && (
                <Typography variant="body2" className={classes.infoText}>
                  Sem turnos = fechado o dia todo
                </Typography>
              )}
              {formDutyShifts.map((shift, i) => (
                <div key={i} className={classes.shiftRow}>
                  <NumberFormat
                    value={shift.start || ""}
                    variant="outlined"
                    margin="dense"
                    customInput={TextField}
                    format="##:##"
                    label="Inicio"
                    size="small"
                    style={{ width: 120 }}
                    onValueChange={(v) => {
                      const updated = [...formDutyShifts];
                      updated[i] = { ...updated[i], start: v.formattedValue };
                      setFormDutyShifts(updated);
                    }}
                  />
                  <NumberFormat
                    value={shift.end || ""}
                    variant="outlined"
                    margin="dense"
                    customInput={TextField}
                    format="##:##"
                    label="Fim"
                    size="small"
                    style={{ width: 120 }}
                    onValueChange={(v) => {
                      const updated = [...formDutyShifts];
                      updated[i] = { ...updated[i], end: v.formattedValue };
                      setFormDutyShifts(updated);
                    }}
                  />
                  <IconButton size="small" onClick={() => setFormDutyShifts(formDutyShifts.filter((_, j) => j !== i))} style={{ color: "#e53935" }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setFormDutyShifts([...formDutyShifts, { start: "", end: "" }])}
                disabled={formDutyShifts.length >= 6}
              >
                Adicionar turno
              </Button>
            </>
          )}

          <Divider />

          <Box display="flex" gap={2}>
            <FormControlLabel
              control={<Switch checked={formIsRecurring} onChange={(e) => setFormIsRecurring(e.target.checked)} color="primary" />}
              label="Recorrente"
            />
            <FormControlLabel
              control={<Switch checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} color="primary" />}
              label="Ativo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">Cancelar</Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!formDay || !formMonth || !formName.trim()}
          >
            {selectedHoliday ? "Salvar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Seed State Dialog ═══ */}
      <Dialog
        open={seedStateDialogOpen}
        onClose={() => { setSeedStateDialogOpen(false); setSeedStateResult(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Carregar feriados estaduais</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className={classes.infoText} style={{ marginBottom: 16 }}>
            Selecione o estado para carregar os feriados estaduais oficiais.
            Feriados já cadastrados serão ignorados automaticamente.
          </Typography>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Estado (UF)</InputLabel>
            <Select
              value={seedStateUf}
              onChange={(e) => { setSeedStateUf(e.target.value); setSeedStateResult(null); }}
              label="Estado (UF)"
            >
              {[
                "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
                "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
                "RS", "RO", "RR", "SC", "SP", "SE", "TO"
              ].map((uf) => (
                <MenuItem key={uf} value={uf}>{uf}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {seedStateResult && (
            <Box className={classes.seedResultBox} mt={2}>
              <Typography variant="body2">
                <strong>Resultado ({seedStateResult.uf}):</strong>{" "}
                {seedStateResult.totalCreated} criados, {seedStateResult.totalSkipped} já existiam
                {seedStateResult.createdNames?.length > 0 && (
                  <span> — {seedStateResult.createdNames.join(", ")}</span>
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setSeedStateDialogOpen(false); setSeedStateResult(null); }}
            color="secondary"
          >
            Fechar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSeedState}
            disabled={seedStateLoading || !seedStateUf}
            startIcon={seedStateLoading ? <CircularProgress size={18} /> : <CloudDownloadIcon />}
          >
            Carregar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Import Dialog ═══ */}
      <Dialog open={importDialogOpen} onClose={() => { setImportDialogOpen(false); setImportPreview(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Importar feriados de API externa</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} mb={2} alignItems="center">
            <TextField
              label="Ano"
              type="number"
              value={importYear}
              onChange={(e) => setImportYear(parseInt(e.target.value) || currentYear)}
              variant="outlined"
              size="small"
              style={{ width: 120 }}
              inputProps={{ min: 2000, max: 2100 }}
            />
            <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
              <InputLabel>Fonte</InputLabel>
              <Select value={importSource} onChange={(e) => setImportSource(e.target.value)} label="Fonte">
                <MenuItem value="brasilapi">BrasilAPI</MenuItem>
                <MenuItem value="nagerdate">Nager.Date</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              onClick={handleImportPreview}
              disabled={importLoading}
              startIcon={importLoading ? <CircularProgress size={18} /> : <CloudDownloadIcon />}
            >
              Buscar
            </Button>
          </Box>

          {importPreview && (
            <>
              <Typography variant="body2" className={classes.infoText}>
                Encontrados: {importPreview.totalFound} | Ja cadastrados: {importPreview.alreadyExisting} | Novos: {importPreview.newHolidays} | Fonte: {importPreview.source}
              </Typography>
              <Box className={classes.importPreviewTable}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={importPreview.holidays.filter((h) => !h.alreadyExists).every((_, i) => {
                            const realIndex = importPreview.holidays.findIndex((h, j) => !h.alreadyExists && Object.keys(importSelected).filter(k => importSelected[k]).length > 0);
                            return true;
                          })}
                          onChange={(e) => {
                            const newSelected = {};
                            importPreview.holidays.forEach((h, i) => {
                              if (!h.alreadyExists) newSelected[i] = e.target.checked;
                            });
                            setImportSelected(newSelected);
                          }}
                        />
                      </TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Nome</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.holidays.map((h, i) => (
                      <TableRow key={i} style={{ opacity: h.alreadyExists ? 0.5 : 1 }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={!!importSelected[i]}
                            onChange={(e) => setImportSelected({ ...importSelected, [i]: e.target.checked })}
                            disabled={h.alreadyExists}
                          />
                        </TableCell>
                        <TableCell>{formatDate(h.day, h.month, h.year)}</TableCell>
                        <TableCell>{h.name}</TableCell>
                        <TableCell align="center">
                          {h.alreadyExists ? (
                            <Chip label="Ja cadastrado" size="small" />
                          ) : (
                            <Chip label="Novo" size="small" color="primary" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportDialogOpen(false); setImportPreview(null); }} color="secondary">
            Fechar
          </Button>
          {importPreview && importPreview.newHolidays > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleImportConfirm}
              disabled={importLoading || Object.values(importSelected).filter(Boolean).length === 0}
              startIcon={importLoading ? <CircularProgress size={18} /> : <PublishIcon />}
            >
              Importar selecionados ({Object.values(importSelected).filter(Boolean).length})
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══ Delete Confirmation ═══ */}
      <ConfirmationModal
        title="Excluir feriado"
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setDeletingHoliday(null); }}
        onConfirm={() => handleDelete(deletingHoliday?.id)}
      >
        {deletingHoliday && (
          <p>
            Tem certeza que deseja excluir o feriado{" "}
            <strong>{deletingHoliday.name || deletingHoliday.description}</strong> (
            {formatDate(deletingHoliday.day, deletingHoliday.month, deletingHoliday.year)})?
          </p>
        )}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Holidays;
